import {
  homeHighlights,
  processSteps,
  servicePillars,
  studioStats,
} from '../data/siteContent';
import HeroOrbital from './HeroOrbital';
import ProgrammingLanguageShowcase from './ProgrammingLanguageShowcase';
import SectionIntro from './SectionIntro';

function HomePage({ brandLogo, onOpenProjects }) {
  const contactApps = [
    {
      id: 'facebook',
      label: 'Facebook',
      href: 'https://www.facebook.com/atacodehouse',
      accent: 'is-facebook',
    },
    {
      id: 'telegram',
      label: 'Telegram',
      href: 'https://t.me/atacodehouse',
      accent: 'is-telegram',
    },
    {
      id: 'viber',
      label: 'Viber',
      href: 'viber://chat?number=%2B959000000000',
      accent: 'is-viber',
    },
  ];

  return (
    <>
      <section className="hero-section">
        <div className="hero-copy">
          <p className="eyebrow">ATA Code House Studio</p>
          <h1>Professional web, app, bot, and UI systems for serious clients.</h1>
          <p className="hero-text">
            ATA Code House builds dependable business websites, polished app demos, automation
            flows, and professional interfaces that help clients trust what they are seeing from
            the very first screen.
          </p>

          <div className="hero-actions">
            <button className="primary-button" onClick={onOpenProjects} type="button">
              Show Demo Project
            </button>
            <a className="secondary-button" href="#contact">
              Start Your Project
            </a>
          </div>
        </div>

        <HeroOrbital brandLogo={brandLogo} />
      </section>

      <section className="content-section">
        <SectionIntro
          eyebrow="Technology stack"
          title="Tools we use to turn ideas into stable, modern products"
          text="From frontend interfaces to backend services and cross-platform apps, ATA Code House works with practical technologies that support reliable delivery."
        />
        <ProgrammingLanguageShowcase />
      </section>

      <section className="content-section">
        <SectionIntro
          eyebrow="Studio metrics"
          title="A coding-first website with strong product storytelling"
          text="The studio landing page now speaks in a more professional software language, balancing visual hierarchy, motion, and client trust."
        />
        <div className="hero-panel-grid">
          {studioStats.map((item) => (
            <article className="metric-card" key={item.label}>
              <strong>{item.value}</strong>
              <span>{item.label}</span>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section">
        <SectionIntro
          eyebrow="Service pillars"
          title="Built for mobile apps, websites, bots, automation, and polished UI UX"
          text="Each service card is framed to feel more like a real delivery capability than a simple portfolio bullet list."
        />
        <div className="service-grid">
          {servicePillars.map((service) => (
            <article className="service-card" key={service.title}>
              <span className="service-code">{service.code}</span>
              <h3>{service.title}</h3>
              <p>{service.description}</p>
              <ul className="feature-list">
                {service.points.map((point) => (
                  <li key={point}>{point}</li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section showcase-section">
        <SectionIntro
          eyebrow="Security confidence"
          title="Built to feel trustworthy, stable, and ready for real client review"
          text="Every showcase is structured to communicate product clarity, safer interaction patterns, smooth flow control, and a presentation quality that feels dependable in front of customers."
        />
        <div className="showcase-feature-grid">
          {homeHighlights.map((item) => (
            <article className="highlight-card" key={item.title}>
              <h3>{item.title}</h3>
              <p>{item.text}</p>
            </article>
          ))}
        </div>
        <div className="showcase-cta-row">
          <button className="primary-button" onClick={onOpenProjects} type="button">
            Show Demo Project
          </button>
        </div>
      </section>

      <section className="content-section">
        <SectionIntro
          eyebrow="Working process"
          title="From strategy to shipped experience"
          text="The structure now reflects a professional studio workflow, from requirement framing through UI motion, component systems, and real showcase delivery."
        />
        <div className="process-grid">
          {processSteps.map((step) => (
            <article className="process-card" key={step.step}>
              <span>{step.step}</span>
              <h3>{step.title}</h3>
              <p>{step.text}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="content-section contact-section" id="contact">
        <div className="contact-card">
          <div>
            <SectionIntro
              eyebrow="Ready to build"
              title="ATA Code House can turn ideas into branded, real-feeling product demos"
              text="Use this site to present your services, show client-ready demos, and invite businesses to contact your studio for app, website, bot, and UI UX work."
            />
          </div>
          <div className="contact-panel">
            <h3 className="contact-panel-title">ATA Code House Contact Channel</h3>
            <div className="contact-line">
              <span>Email</span>
              <strong>atacodehouse@gmail.com</strong>
            </div>
            <div className="contact-line">
              <span>Delivery Scope</span>
              <strong>React websites, mobile app showcases, automation bots, advanced UI UX</strong>
            </div>
            <div className="contact-line">
              <span>Presentation Style</span>
              <strong>Programming-focused, branded, smooth, interactive, and professional</strong>
            </div>
            <div className="contact-social-block">
              <span>Contact apps</span>
              <div className="contact-social-row">
                {contactApps.map((app) => (
                  <a
                    aria-label={`Open ATA Code House ${app.label}`}
                    className={`contact-social-link ${app.accent}`}
                    href={app.href}
                    key={app.id}
                    rel={app.href.startsWith('http') ? 'noreferrer' : undefined}
                    target={app.href.startsWith('http') ? '_blank' : undefined}
                  >
                    <SocialAppIcon id={app.id} label={app.label} />
                  </a>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

function SocialAppIcon({ id, label }) {
  if (id === 'facebook') {
    return (
      <svg
        aria-hidden="true"
        className="contact-social-icon"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="M13.5 21v-7h2.4l.4-3h-2.8V9.1c0-.9.3-1.6 1.6-1.6H16V4.8c-.3 0-.9-.1-1.8-.1-2.9 0-4.7 1.7-4.7 4.9V11H7v3h2.5v7h4Z" />
      </svg>
    );
  }

  if (id === 'telegram') {
    return (
      <svg
        aria-hidden="true"
        className="contact-social-icon"
        viewBox="0 0 24 24"
        fill="currentColor"
      >
        <path d="m19.5 5.2-15 5.8c-1 .4-1 1 .2 1.4l3.9 1.2 1.5 4.7c.2.7.1 1 .8 1 .5 0 .8-.2 1.1-.6l2.1-2 4.3 3.2c.8.4 1.3.2 1.5-.8l2.6-12.2c.3-1.1-.4-1.6-1-1.3Zm-9.7 8.1 8-5c.4-.2.7-.1.4.2l-6.6 6-2.9-.9c-.6-.2-.6-.6.1-.9Z" />
      </svg>
    );
  }

  return (
    <svg
      aria-hidden="true"
      className="contact-social-icon"
      viewBox="0 0 24 24"
      fill="currentColor"
    >
      <path d="M16.2 3.7A7.3 7.3 0 0 0 12 2.5c-4.2 0-7.6 2.7-7.6 6.1 0 2.2 1.5 4.2 3.7 5.3l-.9 4 4.1-2.3c.2 0 .5.1.7.1 4.2 0 7.6-2.7 7.6-6.1 0-2.3-1.5-4.4-3.4-5.9Zm-.3 7.1-1.5 2.4c-.2.4-.7.4-1 .1l-1.7-1.6-1.7 1.6c-.3.3-.8.2-1-.1l-1.5-2.4c-.2-.3 0-.7.4-.7h1.4l1.2-1.9c.2-.3.7-.3.9 0l1.2 1.9h1.4c.4 0 .6.4.4.7Z" />
    </svg>
  );
}

export default HomePage;
